// SCR-STAFF-876 (AP-INV-03؛ US-INV-042، 050 تا 053): خرید — درخواست خرید با اقلام و بهای برآوردی و ارسال برای تأیید (بیش از سقف ← منتظر تأیید
// Manager، BR-INV-050)، تأیید یا رد، سفارش از درخواست تأییدشده با قیمت پیش‌فرض قرارداد یا آخرین خرید، صدور و PDF، رسید از سفارش با تحویل جزئی،
// فاکتور خرید با تطبیق رسید و اختلاف قیمت (BR-INV-051) و تبدیل پیشنهادهای سفارش به درخواست با یک اقدام (AC-INV-042.1).
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type InvItemUnit, type InvPurchaseRequestStatus, type InvPurchaseRequestView, type InvReorderSuggestionView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, FormDialog, JalaliDate, ListPage, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { WriteNotice } from '../settings/common';
import { parseJalaliInput } from '../patients/common';
import { MoneyCell, procurementKeys, ReasonDialog, REQUEST_STATUSES, requestTone, rialsOf, SupplierSelect, unitsOf } from './procurementCommon';
import { OrderCreateDialog, PurchaseOrdersTab } from './PurchaseOrdersTab';
import { PurchaseInvoicesTab } from './PurchaseInvoicesTab';
import { toMilli, useItemOptions, useQuantityText, useWarehouses, WarehouseSelect, warehouseName } from './stockCommon';

export const SCR_STAFF_876 = 'SCR-STAFF-876';

type PurchasingTab = 'requests' | 'orders' | 'invoices' | 'suggestions';

export function PurchasingPage() {
  const { t } = useTranslation('inv');
  const access = useAccess();
  const [params, setParams] = useSearchParams();
  const focusRequestId = params.get('request');
  const tabs: PurchasingTab[] = [
    ...(['inv.purchase_request.create', 'inv.purchase_request.approve', 'inv.po.manage'].some((code) => access.can(code)) ? (['requests'] as const) : []),
    ...(['inv.po.manage', 'inv.receipt.manage', 'inv.supplier_invoice.manage'].some((code) => access.can(code)) ? (['orders'] as const) : []),
    ...(access.can('inv.supplier_invoice.manage') ? (['invoices'] as const) : []),
    ...(access.can('inv.purchase_request.create') && access.can('inv.stock.view') ? (['suggestions'] as const) : []),
  ];
  const [selected, setSelected] = useState<PurchasingTab | null>(null);
  const tab: PurchasingTab | undefined = focusRequestId && tabs.includes('requests') ? 'requests' : (selected ?? tabs[0]);

  return (
    <ListPage
      screenId={SCR_STAFF_876}
      titleKey="inv:purchasing.title"
      subtitleKey="inv:purchasing.subtitle"
      filters={
        <Tabs
          value={tab ?? false}
          onChange={(_event, value: PurchasingTab) => {
            setSelected(value);
            if (focusRequestId) {
              setParams({});
            }
          }}
          aria-label={t('inv:purchasing.title')}
          variant="scrollable"
        >
          {tabs.map((value) => (
            <Tab key={value} value={value} label={t(`inv:purchasing.tabs.${value}`)} data-testid={`purchasing-tab-${value}`} />
          ))}
        </Tabs>
      }
    >
      {tab === 'requests' && <RequestsTab focusId={focusRequestId} onClearFocus={() => setParams({})} />}
      {tab === 'orders' && <PurchaseOrdersTab />}
      {tab === 'invoices' && <PurchaseInvoicesTab />}
      {tab === 'suggestions' && <SuggestionsTab onCreated={() => setSelected('requests')} />}
    </ListPage>
  );
}

function RequestsTab({ focusId, onClearFocus }: { focusId: string | null; onClearFocus: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const warehouses = useWarehouses();
  const quantity = useQuantityText();
  const write = useWriteAccess('inv.purchase_request.create');
  const full = access.accessMode === 'Full';
  const [status, setStatus] = useState<InvPurchaseRequestStatus | ''>('');
  const [creating, setCreating] = useState(false);
  const [ordering, setOrdering] = useState<InvPurchaseRequestView | null>(null);
  const [deciding, setDeciding] = useState<{ request: InvPurchaseRequestView; verb: 'reject' | 'cancel' } | null>(null);

  const list = useQuery({
    queryKey: [...procurementKeys.requests(session?.tenantId), status],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/purchase-requests', { params: { query: { status: status || undefined, limit: 50 } } })),
    enabled: !focusId,
  });
  const focused = useQuery({
    queryKey: [...procurementKeys.requests(session?.tenantId), 'focus', focusId],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/purchase-requests/{id}', { params: { path: { id: focusId ?? '' } } })),
    enabled: Boolean(focusId),
  });
  const idempotency = useIdempotencyKey();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: procurementKeys.requests(session?.tenantId) });
  const act = useMutation({
    mutationFn: ({ request, verb }: { request: InvPurchaseRequestView; verb: 'submit' | 'approve' }) => {
      const options = { params: { path: { id: request.id } }, headers: { 'Idempotency-Key': idempotency.current() } };
      return unwrap(
        verb === 'submit'
          ? tenantApi.POST('/api/v1/inventory/purchase-requests/{id}:submit', options)
          : tenantApi.POST('/api/v1/inventory/purchase-requests/{id}:approve', options),
      );
    },
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: refresh,
  });

  const columns: DataTableColumn<InvPurchaseRequestView>[] = [
    { id: 'number', headerKey: 'inv:purchasing.columns.number', cell: (row) => <bdi dir="ltr" data-testid={`request-${row.number}`}>{row.number}</bdi> },
    {
      id: 'status',
      headerKey: 'inv:purchasing.columns.status',
      cell: (row) => (
        <span data-testid={`request-status-${row.number}`}>
          <StatusChip tone={requestTone(row.status)} labelKey={`inv:request_status.${row.status}`} />
        </span>
      ),
    },
    { id: 'warehouse', headerKey: 'inv:purchasing.columns.warehouse', cell: (row) => warehouseName(warehouses.data?.items, row.warehouseId) },
    { id: 'supplier', headerKey: 'inv:purchasing.columns.supplier', cell: (row) => row.supplierName ?? '—' },
    {
      id: 'lines',
      headerKey: 'inv:purchasing.columns.lines',
      cell: (row) => row.lines.map((line) => `${line.itemName}: ${quantity(line.unitQuantityMilli, line.unit)}`).join(t('common:list_separator')),
    },
    {
      id: 'total',
      headerKey: 'inv:purchasing.columns.estimated_total',
      cell: (row) => (
        <Stack spacing={0.25}>
          <MoneyCell value={row.estimatedTotal} />
          {row.requiresApproval && (
            <Typography variant="caption" color="warning.main">
              {t('inv:purchasing.over_limit')}
            </Typography>
          )}
        </Stack>
      ),
    },
    { id: 'neededBy', headerKey: 'inv:purchasing.columns.needed_by', cell: (row) => (row.neededBy ? <JalaliDate value={row.neededBy} /> : '—') },
    {
      id: 'actions',
      headerKey: 'inv:purchasing.columns.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {row.status === 'Draft' && (
            <Button size="small" disabled={!write.canWrite || act.isPending} onClick={() => act.mutate({ request: row, verb: 'submit' })} data-testid={`request-submit-${row.number}`}>
              {t('inv:purchasing.submit')}
            </Button>
          )}
          {row.status === 'PendingApproval' && (
            <>
              <Button size="small" disabled={!full || !access.can('inv.purchase_request.approve') || act.isPending} onClick={() => act.mutate({ request: row, verb: 'approve' })} data-testid={`request-approve-${row.number}`}>
                {t('inv:purchasing.approve')}
              </Button>
              <Button size="small" color="error" disabled={!full || !access.can('inv.purchase_request.approve')} onClick={() => setDeciding({ request: row, verb: 'reject' })} data-testid={`request-reject-${row.number}`}>
                {t('inv:purchasing.reject')}
              </Button>
            </>
          )}
          {row.status === 'Approved' && !row.purchaseOrderId && (
            <Button size="small" variant="outlined" disabled={!full || !access.can('inv.po.manage')} onClick={() => setOrdering(row)} data-testid={`request-order-${row.number}`}>
              {t('inv:purchasing.create_order')}
            </Button>
          )}
          {row.purchaseOrderNumber && <bdi dir="ltr">{row.purchaseOrderNumber}</bdi>}
          {['Draft', 'PendingApproval', 'Approved'].includes(row.status) && !row.purchaseOrderId && (
            <Button size="small" color="inherit" disabled={!write.canWrite} onClick={() => setDeciding({ request: row, verb: 'cancel' })} data-testid={`request-cancel-${row.number}`}>
              {t('inv:purchasing.cancel')}
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <WriteNotice write={write} />
      {act.error && <Alert severity="error">{t('inv:purchasing.action_failed')}</Alert>}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)} disabled={!write.canWrite} data-testid="request-create">
          {t('inv:purchasing.add_request')}
        </Button>
        {!focusId && (
          <TextField select size="small" label={t('inv:purchasing.columns.status')} value={status} onChange={(event) => setStatus(event.target.value as InvPurchaseRequestStatus | '')} sx={{ minWidth: 200 }}>
            <MenuItem value="">{t('inv:items.filters.all')}</MenuItem>
            {REQUEST_STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`inv:request_status.${value}`)}
              </MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
      {focusId && (
        <Alert
          severity="info"
          data-testid="request-focus"
          action={
            <Button color="inherit" size="small" onClick={onClearFocus}>
              {t('inv:purchasing.show_all')}
            </Button>
          }
        >
          {t('inv:purchasing.focus')}
        </Alert>
      )}
      <DataTable
        captionKey="inv:purchasing.requests_caption"
        columns={columns}
        rows={focusId ? (focused.data ? [focused.data] : undefined) : list.data?.items}
        getRowId={(row) => row.id}
        isLoading={focusId ? focused.isPending : list.isPending}
        error={focusId ? focused.error : list.error}
        onRetry={() => void (focusId ? focused.refetch() : list.refetch())}
        emptyTitleKey="inv:purchasing.requests_empty"
      />
      {creating && (
        <RequestDialog
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {ordering && (
        <OrderCreateDialog
          request={ordering}
          onClose={() => setOrdering(null)}
          onSaved={() => {
            setOrdering(null);
            refresh();
            void queryClient.invalidateQueries({ queryKey: procurementKeys.orders(session?.tenantId) });
          }}
        />
      )}
      {deciding && (
        <ReasonDialog
          titleKey={deciding.verb === 'reject' ? 'inv:purchasing.reject_title' : 'inv:purchasing.cancel_title'}
          screenId="SCR-STAFF-876-REQUEST-DECISION"
          required={deciding.verb === 'reject'}
          onClose={() => setDeciding(null)}
          onConfirm={async (reason, key) => {
            const options = { params: { path: { id: deciding.request.id } }, body: { reason }, headers: { 'Idempotency-Key': key } };
            await unwrap(
              deciding.verb === 'reject'
                ? tenantApi.POST('/api/v1/inventory/purchase-requests/{id}:reject', options)
                : tenantApi.POST('/api/v1/inventory/purchase-requests/{id}:cancel', options),
            );
            setDeciding(null);
            refresh();
          }}
        />
      )}
    </Stack>
  );
}

interface RequestLineState {
  itemId: string;
  quantity: string;
  unit: InvItemUnit | '';
  unitPrice: string;
}

const emptyRequestLine: RequestLineState = { itemId: '', quantity: '', unit: '', unitPrice: '' };

function RequestDialog({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi } = useTenantSession();
  const idempotency = useIdempotencyKey();
  const items = useItemOptions();
  const [warehouseId, setWarehouseId] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<RequestLineState[]>([{ ...emptyRequestLine }]);
  const itemOf = (id: string) => items.data?.items.find((item) => item.id === id);
  const update = (index: number, patch: Partial<RequestLineState>) => setLines((current) => current.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/purchase-requests', {
          body: {
            warehouseId,
            supplierId: supplierId || null,
            neededBy: parseJalaliInput(neededBy) ?? null,
            note: note.trim() || null,
            lines: lines
              .filter((line) => line.itemId)
              .map((line) => {
                const price = rialsOf(line.unitPrice);
                return {
                  itemId: line.itemId,
                  quantityMilli: toMilli(line.quantity) ?? 0,
                  unit: line.unit || itemOf(line.itemId)?.purchaseUnit || null,
                  ...(price === null ? {} : { unitPrice: { amount: price, currency: 'IRR' } }),
                };
              }),
          },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: onSaved,
  });

  return (
    <FormDialog
      open
      screenId="SCR-STAFF-876-REQUEST-FORM"
      titleKey="inv:purchasing.request_title"
      onSubmit={() => save.mutate()}
      onClose={onClose}
      isDirty={lines.some((line) => line.itemId)}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="lg"
    >
      <Alert severity="info">{t('inv:purchasing.approval_note')}</Alert>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <WarehouseSelect value={warehouseId} onChange={setWarehouseId} testId="request-warehouse" required />
        <SupplierSelect value={supplierId} onChange={setSupplierId} testId="request-supplier" emptyLabelKey="inv:purchasing.no_supplier" />
        <TextField
          size="small"
          label={t('inv:purchasing.needed_by')}
          placeholder="1405/08/01"
          value={neededBy}
          onChange={(event) => setNeededBy(event.target.value)}
          error={Boolean(neededBy.trim()) && !parseJalaliInput(neededBy)}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'request-needed-by' } }}
        />
      </Stack>
      <Typography variant="subtitle2" component="h3">
        {t('inv:documents.lines')}
      </Typography>
      {lines.map((line, index) => {
        const item = itemOf(line.itemId);
        return (
          <Stack key={index} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
            <TextField select size="small" required label={t('inv:stock.columns.item')} value={line.itemId} onChange={(event) => update(index, { itemId: event.target.value, unit: '' })} sx={{ minWidth: 220 }} slotProps={{ htmlInput: { 'data-testid': `request-line-item-${index}` } }}>
              {(items.data?.items ?? []).map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField size="small" required label={t('inv:documents.quantity')} value={line.quantity} onChange={(event) => update(index, { quantity: event.target.value })} sx={{ width: 120 }} slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'decimal', 'data-testid': `request-line-quantity-${index}` } }} />
            <TextField select size="small" label={t('inv:documents.unit')} value={line.unit || item?.purchaseUnit || ''} onChange={(event) => update(index, { unit: event.target.value as InvItemUnit })} sx={{ width: 140 }} disabled={!item}>
              {unitsOf(item).map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {t(`inv:unit.${unit}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={t('inv:purchasing.unit_price')}
              helperText={t('inv:purchasing.unit_price_help')}
              value={line.unitPrice}
              onChange={(event) => update(index, { unitPrice: event.target.value })}
              sx={{ width: 200 }}
              slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': `request-line-price-${index}` } }}
            />
            <IconButton aria-label={t('inv:documents.remove_line')} onClick={() => setLines((current) => current.filter((_line, i) => i !== index))} disabled={lines.length === 1}>
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        );
      })}
      <Stack direction="row">
        <Button startIcon={<AddIcon />} onClick={() => setLines((current) => [...current, { ...emptyRequestLine }])} data-testid="request-add-line">
          {t('inv:documents.add_line')}
        </Button>
      </Stack>
      <TextField size="small" label={t('inv:documents.note')} value={note} onChange={(event) => setNote(event.target.value)} />
    </FormDialog>
  );
}

function SuggestionsTab({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation('inv');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const write = useWriteAccess('inv.purchase_request.create');
  const [warehouseId, setWarehouseId] = useState('');
  const [excluded, setExcluded] = useState<string[]>([]);
  const suggestions = useQuery({
    queryKey: ['inventory', 'reorder-rules', session?.tenantId, warehouseId, 'suggestions'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/reorder-suggestions', { params: { query: { warehouseId } } })),
    enabled: Boolean(warehouseId),
  });
  const chosen = (suggestions.data?.items ?? []).filter((row) => !excluded.includes(row.itemId));
  const convert = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/inventory/purchase-requests:from-suggestions', {
          body: { warehouseId, itemIds: chosen.map((row) => row.itemId) },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: procurementKeys.requests(session?.tenantId) });
      onCreated();
    },
  });
  const columns: DataTableColumn<InvReorderSuggestionView>[] = [
    {
      id: 'pick',
      headerKey: 'inv:purchasing.columns.pick',
      cell: (row) => (
        <Checkbox
          checked={!excluded.includes(row.itemId)}
          onChange={(event) => setExcluded((current) => (event.target.checked ? current.filter((id) => id !== row.itemId) : [...current, row.itemId]))}
          slotProps={{ input: { 'aria-label': row.itemName } }}
        />
      ),
    },
    { id: 'item', headerKey: 'inv:stock.columns.item', cell: (row) => row.itemName },
    { id: 'onHand', headerKey: 'inv:stock.columns.on_hand', cell: (row) => faDigits(Number(row.onHandMilli) / 1000) },
    { id: 'open', headerKey: 'inv:purchasing.columns.open_orders', cell: (row) => faDigits(Number(row.openOrderMilli) / 1000) },
    {
      id: 'suggested',
      headerKey: 'inv:reorder.columns.suggested',
      cell: (row) => (
        <span data-testid={`purchase-suggestion-${row.itemId}`}>
          {faDigits(Number(row.suggestedPurchaseUnits))} {t(`inv:unit.${row.purchaseUnit}`)}
        </span>
      ),
    },
  ];
  return (
    <Stack spacing={2}>
      <WriteNotice write={write} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <WarehouseSelect value={warehouseId} onChange={setWarehouseId} testId="suggestions-warehouse" />
        <Button variant="contained" disabled={!write.canWrite || !warehouseId || chosen.length === 0 || convert.isPending} onClick={() => convert.mutate()} data-testid="suggestions-to-request">
          {t('inv:purchasing.suggestions_to_request')}
        </Button>
      </Stack>
      {convert.error && <Alert severity="error">{t('inv:purchasing.action_failed')}</Alert>}
      {warehouseId ? (
        <DataTable
          captionKey="inv:purchasing.suggestions_caption"
          columns={columns}
          rows={suggestions.data?.items}
          getRowId={(row) => row.itemId}
          isLoading={suggestions.isPending}
          error={suggestions.error}
          emptyTitleKey="inv:purchasing.suggestions_empty"
        />
      ) : (
        <Alert severity="info">{t('inv:purchasing.choose_warehouse')}</Alert>
      )}
    </Stack>
  );
}
